import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

// Per-account cart isolation.
//
// Only anonymous buyers and authenticated customers may own cart state:
//   • guest      → key `arctic_cart_v1__guest`
//   • customer   → key `arctic_cart_v1__u_<user_id>`
//   • seller/admin/unknown auth state → no slot, no persistence, empty cart
//
// Guest items migrate only on guest → customer sign-in. They are discarded on
// guest → seller/admin sign-in so a privileged account never inherits buyer
// state. During auth bootstrap the provider exposes an empty cart rather than
// briefly rendering a previous anonymous shopper's items.
const LEGACY_KEY = "arctic_cart_v1";
const GUEST_KEY = "arctic_cart_v1__guest";
const userKey = (userId) => `arctic_cart_v1__u_${userId}`;
const EMPTY = Object.freeze({ vendor: null, items: [], drop: null });

const _readSlot = (key) => {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && Array.isArray(parsed.items)) {
      return parsed;
    }
  } catch { /* ignore corrupt json */ }
  return null;
};

const _writeSlot = (key, cart) => {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(cart)); }
  catch { /* private mode / quota exceeded — silently ok */ }
};

const _clearSlot = (key) => {
  if (!key) return;
  try { localStorage.removeItem(key); }
  catch { /* ignore */ }
};

const _migrateLegacyGuestCart = () => {
  _clearSlot(LEGACY_KEY);
};

const _isCustomer = (user) => Boolean(user && user.id && user.role === "customer");
const _canUseCart = (user) => user === false || _isCustomer(user);
const _slotFor = (user) => {
  if (user === false) return GUEST_KEY;
  if (_isCustomer(user)) return userKey(user.id);
  return null;
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const activeSlotRef = useRef(null);
  const [cart, setCart] = useState(() => {
    _migrateLegacyGuestCart();
    return EMPTY;
  });
  const canUseCart = _canUseCart(user);

  useEffect(() => {
    _migrateLegacyGuestCart();

    if (user === null) {
      activeSlotRef.current = null;
      setCart(EMPTY);
      return;
    }

    const previousSlot = activeSlotRef.current;
    const nextSlot = _slotFor(user);

    if (!nextSlot) {
      _clearSlot(GUEST_KEY);
      if (user && user.id) _clearSlot(userKey(user.id));
      activeSlotRef.current = null;
      setCart(EMPTY);
      return;
    }

    if (nextSlot === previousSlot) return;

    let next = _readSlot(nextSlot) || EMPTY;
    const isGuestToCustomer = previousSlot === GUEST_KEY && _isCustomer(user);
    if (isGuestToCustomer) {
      const guestCart = _readSlot(GUEST_KEY);
      if (guestCart && guestCart.items.length > 0 && next.items.length === 0) {
        next = guestCart;
        _writeSlot(nextSlot, next);
      }
      _clearSlot(GUEST_KEY);
    }

    activeSlotRef.current = nextSlot;
    setCart(next);
  }, [user]);

  // Persist synchronously with each explicit buyer mutation instead of through
  // a generic state effect. This prevents an identity transition from ever
  // writing the previous account's in-memory cart into the newly selected slot.
  const addItem = useCallback((vendor, item, options = {}) => {
    if (!canUseCart || !activeSlotRef.current) return;
    const incomingDrop = options?.drop || null;
    setCart((prev) => {
      let next;
      const vendorChanged = Boolean(prev.vendor && prev.vendor.id !== vendor.id);
      const previousDropId = prev.drop?.id || null;
      const incomingDropId = incomingDrop?.id || null;
      const commerceContextChanged = previousDropId !== incomingDropId;

      // Never mix a limited Drop cart with the seller's regular storefront
      // cart (or with another Drop). Inventory rules would become ambiguous.
      if (vendorChanged || commerceContextChanged) {
        next = { vendor, items: [{ ...item, quantity: 1 }], drop: incomingDrop };
      } else {
        const existing = prev.items.find((i) => i.id === item.id);
        if (existing) {
          next = {
            vendor: prev.vendor || vendor,
            drop: prev.drop || incomingDrop,
            items: prev.items.map((i) =>
              i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i,
            ),
          };
        } else {
          next = {
            vendor: prev.vendor || vendor,
            drop: prev.drop || incomingDrop,
            items: [...prev.items, { ...item, quantity: 1 }],
          };
        }
      }
      _writeSlot(activeSlotRef.current, next);
      return next;
    });
  }, [canUseCart]);

  const updateQty = useCallback((itemId, qty) => {
    if (!canUseCart || !activeSlotRef.current) return;
    setCart((prev) => {
      let next;
      if (qty <= 0) {
        const items = prev.items.filter((i) => i.id !== itemId);
        next = { vendor: items.length ? prev.vendor : null, items, drop: items.length ? (prev.drop || null) : null };
      } else {
        next = {
          ...prev,
          items: prev.items.map((i) => (i.id === itemId ? { ...i, quantity: qty } : i)),
        };
      }
      _writeSlot(activeSlotRef.current, next);
      return next;
    });
  }, [canUseCart]);

  const clear = useCallback(() => {
    if (activeSlotRef.current) _clearSlot(activeSlotRef.current);
    setCart(EMPTY);
  }, []);

  const visibleCart = canUseCart ? cart : EMPTY;
  const total = visibleCart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const count = visibleCart.items.reduce((sum, item) => sum + item.quantity, 0);

  const value = useMemo(
    () => ({ cart: visibleCart, addItem, updateQty, clear, total, count, canUseCart }),
    [visibleCart, addItem, updateQty, clear, total, count, canUseCart],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
